import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, Popover, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Link, useAuthProvider, useSidebarState, useTranslate } from 'react-admin';
import { useQueryClient } from 'react-query';

import { AssociatedAccounts, ZodAssociatedAccounts } from '@Common/types/auth/associated_accounts';

import { Avatar } from '@sh4res/bo-common/components/Avatar';
import { getTheme } from '@sh4res/bo-common/theme';

import { setDefaultAdminActive } from '@Components/LoginPageForm';
import { authClient } from '@Utils/authClient';
import { STORAGE_KEYS } from '@Utils/storage_keys';

export const CompanySelector = () => {
  const [sidebarIsOpen] = useSidebarState();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const translate = useTranslate();

  const closePopover = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const styleMinimized = useMemo(
    () =>
      sidebarIsOpen
        ? {}
        : {
            border: 'none',
            borderRadius: 'none',
            marginLeft: '7px',
          },
    [sidebarIsOpen],
  );

  const activeAdminIdStorage = localStorage.getItem(STORAGE_KEYS.activeAdminId);
  const adminsListStorage = localStorage.getItem(STORAGE_KEYS.adminsList);

  const adminsList = useMemo(() => {
    if (activeAdminIdStorage === null || adminsListStorage === null) {
      return null;
    }
    const parsed = ZodAssociatedAccounts.safeParse(JSON.parse(adminsListStorage));
    if (parsed.error) {
      /* oxlint-disable no-console */
      /* eslint-disable no-console */
      console.log('Error while parsing admins list', parsed.error);
      return null;
    }
    return parsed.data;
  }, [activeAdminIdStorage, adminsListStorage]);

  const activeAdminId = activeAdminIdStorage ? Number(activeAdminIdStorage) : null;
  const companyName = adminsList?.find((admin) => admin.id === activeAdminId)?.name ?? '';
  const isMultipleAdmins = adminsList ? adminsList.length > 1 : false;

  const openPopover = useCallback(
    (target: HTMLElement | null) => isMultipleAdmins && setAnchorEl(target),
    [setAnchorEl, isMultipleAdmins],
  );

  if (!adminsList || activeAdminId === null) {
    return null;
  }

  return (
    <>
      <ButtonBase
        id="company-selector"
        data-testid="BoExternalCompanySelector_SelectorButton"
        aria-label={translate('resources.admin_selector.accessibility.current_company', {
          name: companyName,
        })}
        {...(isMultipleAdmins && {
          'aria-haspopup': 'listbox',
          'aria-expanded': isOpen,
          'aria-controls': isOpen ? 'company-selector-popover' : undefined,
        })}
        onClick={(event) => openPopover(event.currentTarget)}
        sx={(theme) => ({
          border: `1px solid ${getTheme() === 'axa' ? '#E5E5E5' : 'rgba(255,255,255,0.15)'}`,
          color: getTheme() === 'axa' ? 'rgba(0,0,0,0.87)' : '#808080',
          borderRadius: '12px',
          margin: '12px 10px 0px 10px',
          display: 'flex',
          flexDirection: 'row',
          height: '45px',
          alignItems: 'center',
          cursor: isMultipleAdmins ? 'pointer' : 'default',
          '&:focus-visible': {
            backgroundColor: theme.palette.action.focus,
            outline: 'none',
          },
          ...styleMinimized,
        })}>
        <Avatar
          sx={{ width: '30px', height: '30px', margin: '0px 2px 0px 6px' }}
          fullName={companyName}
        />
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '15.5px',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            width: '100%',
            textAlign: 'left',
          }}>
          {companyName}
        </Typography>
        {isMultipleAdmins && (
          <ExpandMoreIcon style={{ width: '22px', height: '22px', marginRight: '5px' }} />
        )}
      </ButtonBase>

      <Popover
        id="company-selector-title"
        aria-labelledby="company-selector-popover"
        open={isOpen}
        onClose={closePopover}
        anchorEl={anchorEl}
        style={{
          marginLeft: '-5px',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              borderRadius: '14px',
            },
          },
        }}>
        <CompanySelectorPopup adminsList={adminsList} activeAdminId={activeAdminId} />
      </Popover>
    </>
  );
};

const CompanySelectorPopup = ({
  adminsList,
  activeAdminId,
}: {
  adminsList: AssociatedAccounts;
  activeAdminId: number;
}) => {
  const translate = useTranslate();
  const authProvider = useAuthProvider();
  const queryClient = useQueryClient();

  const setAdminActive = useCallback(
    async (adminId: number) => {
      const { error } = await authClient.users.active({ userId: adminId });
      if (error) {
        localStorage.removeItem(STORAGE_KEYS.activeAdminId);
        localStorage.removeItem(STORAGE_KEYS.adminsList);
        await setDefaultAdminActive();
      } else {
        localStorage.setItem(STORAGE_KEYS.activeAdminId, String(adminId));
      }
      await authProvider.resetCache();
      queryClient.clear();
      window.location.replace('/');
    },
    [authProvider, queryClient],
  );

  return (
    <Box
      role="listbox"
      aria-label={translate('resources.admin_selector.accessibility.companies')}
      sx={{
        width: '350px',
        overflow: 'hidden',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Typography
        id="company-selector-title"
        sx={{ height: '35px', marginTop: '20px', marginLeft: '15px' }}>
        {translate('resources.admin_selector.title')}
      </Typography>

      {adminsList.map((admin) => (
        <ButtonBase
          key={admin.id}
          data-testid="BoExternalCompanySelectorPopup_AdminOptionButton"
          role="option"
          aria-selected={admin.id === activeAdminId}
          aria-label={translate('resources.admin_selector.accessibility.current_company', {
            name: admin.name,
          })}
          tabIndex={0}
          onClick={async () => setAdminActive(admin.id)}
          sx={(theme) => ({
            borderTop: '0.5px solid #E5E5E5',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            '&:hover': { background: '#0000000A' },
            '&:focus-visible': {
              backgroundColor: theme.palette.action.focus,
              outline: 'none',
            },
          })}>
          <Avatar
            sx={{ width: '40px', height: '40px', margin: '0px 2px 0px 15px' }}
            fullName={admin.name}
          />
          <Typography
            sx={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              width: '100%',
              textAlign: 'left',
            }}>
            {admin.name}
          </Typography>
          <ChevronRightIcon style={{ width: '22px', height: '22px', marginRight: '10px' }} />
        </ButtonBase>
      ))}

      <Typography
        sx={{
          borderTop: '0.5px solid #E5E5E5',
          fontSize: '13px',
          height: '30px',
          margin: '0px',
          paddingTop: '8px',
          textAlign: 'center',
        }}>
        {translate('resources.admin_selector.contact_text')},
        <Link
          to="/contact_support"
          style={{
            textDecoration: 'underline',
          }}>
          {translate('resources.admin_selector.contact_link')}
        </Link>
      </Typography>
    </Box>
  );
};
